import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useChannelStore } from "@/stores/channelStore";
import { ChannelForm } from "./ChannelForm";
import { useCreateChannel, useUpdateChannel, useDeleteChannel, useDeleteChannels } from "@/hooks/useChannels";
import { toast } from "sonner";
import type { ChannelFormData } from "../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { channelToFormData } from '../utils/transforms';
import { useProvider } from "@/hooks/useProvider";
import { useEffect } from "react";

interface ChannelModalsProps {
  providerId: number;
  versionId: number;
}

export function ChannelModals({ providerId, versionId }: ChannelModalsProps) {
  const {
    isCreateModalOpen,
    closeCreateModal,
    isEditModalOpen,
    closeEditModal,
    isDeleteModalOpen,
    closeDeleteModal,
    editingChannel: selectedChannel,
    selectedChannels,
    currentRegionId
  } = useChannelStore();

  const createChannel = useCreateChannel();
  const updateChannel = useUpdateChannel();
  const deleteChannel = useDeleteChannel();
  const deleteChannels = useDeleteChannels();
  const { data: provider } = useProvider(providerId);

  // DEBUG: Log mutation state changes
  useEffect(() => {
    console.log('[DEBUG] updateChannel.isPending changed:', updateChannel.isPending);
  }, [updateChannel.isPending]);

  useEffect(() => {
    console.log('[DEBUG] updateChannel.status changed:', updateChannel.status);
  }, [updateChannel.status]);

  // DEBUG: Log modal state changes
  useEffect(() => {
    console.log('[DEBUG] isEditModalOpen changed:', isEditModalOpen);
  }, [isEditModalOpen]);

  // Reset mutations when modals close to prevent stale loading states
  const handleCloseEditModal = () => {
    console.log('[DEBUG] handleCloseEditModal: Resetting mutation');
    updateChannel.reset();
    console.log('[DEBUG] handleCloseEditModal: Calling closeEditModal from store');
    closeEditModal();
    console.log('[DEBUG] handleCloseEditModal: closeEditModal called');

    // Robustly restore interactivity in case a modal layer leaves body inert
    const restore = () => {
      try {
        // Prefer explicitly enabling pointer events
        document.body.style.pointerEvents = 'auto';
        document.documentElement.style.pointerEvents = 'auto';
        // Clear scroll lock attribute if left behind by a modal stack
        document.body.removeAttribute('data-scroll-locked');
      } catch {}
    };

    // Run a short sequence to outlast any late cleanup from overlays
    requestAnimationFrame(restore);
    setTimeout(restore, 50);
    setTimeout(restore, 150);
  };

  const handleCloseCreateModal = () => {
    createChannel.reset();
    closeCreateModal();
  };

  const handleCloseDeleteModal = () => {
    deleteChannel.reset();
    deleteChannels.reset();
    closeDeleteModal();
  };

  const handleCreateSubmit = async (data: ChannelFormData) => {
    console.log('[ChannelModals] Create Submit:', {
      formData: data,
      price: data.price,
      description: data.description,
      categoryIds: data.category_ids,
      defaultCategoryId: data.default_category_id,
      regionId: currentRegionId
    });

    // Check the transformation of form data to mutation input
    const mutationInput = {
      providerId,
      versionId,
      name: data.name,
      number: data.numbers[0].number,
      price: data.price,
      description: data.description,
      categoryId: data.category_id, // Keep for backward compatibility
      categoryIds: data.category_ids, // Add multiple categories
      defaultCategoryId: data.default_category_id, // Add default category
      planIds: data.plan_ids,
      is_local: currentRegionId ? data.is_local : false // Only use is_local when there's a region context
    };
    
    console.log('[ChannelModals] Mutation Input:', mutationInput);
    
    try {
      if (!provider) throw new Error('Provider not found');

      const channelData = {
        providerId,
        versionId,
        name: data.name,
        categoryId: data.category_id,
        categoryIds: data.category_ids,
        defaultCategoryId: data.default_category_id,
        numbers: provider.has_sequence_numbers ? [] : data.numbers,
        planIds: data.plan_ids,
        tagIds: data.tag_ids || [],
        price: data.price,
        description: data.description,
        is_local: currentRegionId ? data.is_local : false, // Only use is_local when there's a region context
        is_duplicate: data.is_duplicate || false // Include is_duplicate flag
      };

      await createChannel.mutateAsync(channelData);

      toast.success("Channel Created", {
        description: "New channel has been added successfully",
        duration: 3000,
      });
      handleCloseCreateModal();
    } catch (error) {
      console.error('Create channel error:', error);
      
      if (error instanceof Error) {
        toast.error("Duplicate Channel", {
          description: error.message,
          duration: 4000,
        });
      } else {
        toast.error("Channel Creation Failed", {
          description: "An unexpected error occurred",
          duration: 4000,
        });
      }
    }
  };

  const handleEditSubmit = async (data: ChannelFormData) => {
    console.log('[ChannelModals] Edit form data:', {
      ...data,
      categoryId: data.category_id,
      categoryIds: data.category_ids,
      defaultCategoryId: data.default_category_id,
      price: data.price,
      description: data.description,
      is_local: data.is_local
    });
    
    try {
      if (!selectedChannel) throw new Error('Channel not found');
      
      // Make a direct API call to get channel info if available
      try {
        const response = await fetch(`/api/debug/channel/${selectedChannel.id}`);
        if (response.ok) {
          const channelData = await response.json();
          console.log('[ChannelModals] Direct API response:', channelData);
        }
      } catch (apiError) {
        console.log('API check not available', apiError);
      }

      // Log the current state of form data and channel data for debugging
      console.log('[ChannelModals] Current channel data before update:', {
        id: selectedChannel.id,
        categories: selectedChannel.categories,
        currentCategoryIds: selectedChannel.categories?.map(c => c.category_id) || [],
        is_local: selectedChannel.is_local
      });

      const updateData = {
        id: selectedChannel.id,
        providerId,
        versionId,
        name: data.name,
        numbers: data.numbers.map(n => ({
          typeId: n.typeId,
          number: n.number,
          is_sequence: n.is_sequence,
          is_default: n.is_default
        })),
        categoryId: data.category_id,
        categoryIds: data.category_ids || [],
        defaultCategoryId: data.default_category_id || (data.category_ids && data.category_ids.length > 0 ? data.category_ids[0] : null),
        description: data.description,
        planIds: data.plan_ids,
        tagIds: data.tag_ids || [],
        price: data.price,
        is_local: currentRegionId ? data.is_local : false, // Include is_local in update data when in a region context
        is_duplicate: data.is_duplicate || false // Include is_duplicate flag
      };
      
      // Log the data being sent to the update mutation
      console.log('[ChannelModals] Sending update data:', updateData);

      await updateChannel.mutateAsync(updateData);

      console.log('[DEBUG] Mutation completed, showing success toast');
      toast.success("Channel Updated", {
        description: "Channel information has been saved successfully",
        duration: 3000,
      });
      console.log('[DEBUG] Calling handleCloseEditModal');
      handleCloseEditModal();
      console.log('[DEBUG] handleCloseEditModal called');
    } catch (error) {
      console.error('Update channel error:', error);
      
      if (error instanceof Error) {
        toast.error("Channel Update Failed", {
          description: error.message,
          duration: 4000,
        });
      } else {
        toast.error("Update Failed", {
          description: "An unexpected error occurred",
          duration: 4000,
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedChannel) {
        await deleteChannel.mutateAsync({
          id: selectedChannel.id,
          providerId,
          versionId
        });
      }
      handleCloseDeleteModal();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete channel(s)");
    }
  };

  const isDeleting = deleteChannel.isPending || deleteChannels.isPending;
  const selectedCount = Object.values(selectedChannels).filter(Boolean).length;
  const deleteCount = selectedCount || (selectedChannel ? 1 : 0);

  return (
    <>
      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={handleCloseCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Channel</DialogTitle>
          </DialogHeader>
          <DialogDescription>
           Description
          </DialogDescription>
          <ChannelForm
            providerId={providerId}
            versionId={versionId}
            onSubmit={handleCreateSubmit}
            isSubmitting={createChannel.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => { if (!open) handleCloseEditModal(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Channel</DialogTitle>
          </DialogHeader>
          <ChannelForm
            providerId={providerId}
            versionId={versionId}
            onSubmit={handleEditSubmit}
            isSubmitting={updateChannel.isPending}
            defaultValues={selectedChannel ? channelToFormData(selectedChannel) : undefined}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={handleCloseDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCount === 1
                ? `This will permanently delete the channel "${selectedChannel?.name}".`
                : `This will permanently delete ${deleteCount} channels.`}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
